import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash, Sparkle, X, Clock, MapPin } from '@phosphor-icons/react';
import { ITINERARY_CATEGORIES, fmtMoney, tripDays } from './TripCard';

export default function ItineraryTab({ api, trip, onTripUpdate }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/trips/${trip.trip_id}/itinerary`);
      setItems(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api, trip.trip_id]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/trips/${trip.trip_id}/itinerary/${id}`);
      setItems(prev => prev.filter(x => x.item_id !== id));
    } catch (_) { /* ignore */ }
  };

  // Group by day_index
  const byDay = {};
  items.forEach(it => {
    const k = it.day_index || 1;
    if (!byDay[k]) byDay[k] = [];
    byDay[k].push(it);
  });
  const dayKeys = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  return (
    <div className="space-y-3" data-testid="itinerary-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-neutral-400">
          {items.length === 0 ? 'No itinerary items yet' : `${items.length} item(s) across ${dayKeys.length} day(s)`}
        </p>
        <div className="flex gap-2">
          <button onClick={() => setAiOpen(true)} data-testid="itinerary-ai-btn"
            className="px-3 py-1.5 rounded-lg bg-purple-500/15 text-purple-300 border border-purple-500/40 text-[11px] font-bold hover:bg-purple-500/25 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> AI itinerary
          </button>
          <button onClick={() => setShowForm(true)} data-testid="itinerary-add-btn"
            className="px-3 py-1.5 rounded-lg bg-[#FF6B35] text-white text-[11px] font-bold hover:bg-[#E85D2A] flex items-center gap-1.5">
            <Plus size={11} weight="bold" /> Add
          </button>
        </div>
      </div>

      {loading && <div className="text-center py-6 text-sm text-neutral-500">Loading…</div>}

      {!loading && items.length === 0 && (
        <div className="rounded-xl p-8 text-center" style={{ background: 'rgba(168,85,247,0.05)', border: '1px dashed rgba(168,85,247,0.30)' }}
          data-testid="itinerary-empty">
          <p className="text-sm text-neutral-300 mb-1">Empty canvas, infinite possibilities.</p>
          <p className="text-[11px] text-neutral-500">Click <b>AI itinerary</b> to let the assistant draft day-by-day plans, or add items manually.</p>
        </div>
      )}

      {!loading && dayKeys.map(d => (
        <div key={d} className="rounded-2xl overflow-hidden" data-testid={`itinerary-day-${d}`}
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="px-4 py-2.5 flex items-center gap-2"
            style={{ background: 'rgba(255,107,53,0.08)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <span className="px-2 py-0.5 rounded-md text-[10px] font-extrabold bg-[#FF6B35] text-white">DAY {d}</span>
            {byDay[d][0]?.day_date && <span className="text-[11px] text-neutral-400">{byDay[d][0].day_date}</span>}
            <span className="text-[10px] text-neutral-500 ml-auto">{byDay[d].length} item(s)</span>
          </div>
          <div className="divide-y divide-white/5">
            {byDay[d].map(it => {
              const cat = ITINERARY_CATEGORIES.find(c => c.id === it.category) || ITINERARY_CATEGORIES[3];
              return (
                <div key={it.item_id} className="px-4 py-3 flex items-start gap-3 group hover:bg-white/[0.02]"
                  data-testid={`itinerary-item-${it.item_id}`}>
                  <div className="text-xl flex-shrink-0">{cat.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      {it.time && <span className="text-[10px] font-mono font-bold text-[#FF6B35] flex items-center gap-1"><Clock size={9} /> {it.time}</span>}
                      <p className="text-sm font-bold text-white">{it.title}</p>
                      {it.ai_generated && <span className="text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30">AI</span>}
                    </div>
                    {it.location && <p className="text-[11px] text-neutral-400 mt-0.5 flex items-center gap-1"><MapPin size={9} weight="fill" /> {it.location}</p>}
                    {it.notes && <p className="text-[11px] text-neutral-500 mt-0.5">{it.notes}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {(it.cost_estimate > 0) && (
                      <span className="text-[10px] text-emerald-300 font-bold">{fmtMoney(it.cost_estimate, trip.currency)}</span>
                    )}
                    <button onClick={() => handleDelete(it.item_id)} data-testid={`itinerary-delete-${it.item_id}`}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded-lg text-neutral-500 hover:bg-red-500/10 hover:text-red-400 transition-all">
                      <Trash size={11} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {showForm && (
        <ItineraryEditModal api={api} trip={trip} maxDay={tripDays(trip)}
          onClose={() => setShowForm(false)}
          onSaved={async () => { setShowForm(false); await fetchItems(); }} />
      )}

      {aiOpen && (
        <AIItineraryModal api={api} trip={trip}
          onClose={() => setAiOpen(false)}
          onDone={async () => { setAiOpen(false); await fetchItems(); onTripUpdate && onTripUpdate(); }} />
      )}
    </div>
  );
}

function ItineraryEditModal({ api, trip, maxDay, onClose, onSaved }) {
  const [form, setForm] = useState({
    day_index: 1,
    time: '',
    title: '',
    location: '',
    notes: '',
    cost_estimate: 0,
    category: 'activity',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setError('');
    if (!form.title.trim()) { setError('Title required'); return; }
    setSaving(true);
    try {
      await api.post(`/trips/${trip.trip_id}/itinerary`, {
        ...form,
        day_index: Number(form.day_index) || 1,
        cost_estimate: Number(form.cost_estimate) || 0,
      });
      onSaved && onSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="itinerary-edit-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Add Itinerary Item</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <L>Day</L>
              <input type="number" min="1" max={maxDay} value={form.day_index}
                onChange={e => setForm(f => ({ ...f, day_index: e.target.value }))}
                data-testid="itin-day" className="t-input" />
            </div>
            <div>
              <L>Time</L>
              <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                data-testid="itin-time" className="t-input" />
            </div>
          </div>
          <div>
            <L>Title</L>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              data-testid="itin-title" className="t-input" placeholder="Visit Baga Beach" />
          </div>
          <div>
            <L>Location</L>
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              data-testid="itin-location" className="t-input" placeholder="North Goa" />
          </div>
          <div>
            <L>Category</L>
            <div className="flex flex-wrap gap-1.5" data-testid="itin-category-chips">
              {ITINERARY_CATEGORIES.map(c => (
                <button key={c.id} type="button" onClick={() => setForm(f => ({ ...f, category: c.id }))}
                  data-testid={`itin-cat-${c.id}`}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${form.category === c.id ? 'bg-[#FF6B35]/20 text-[#FF6B35] border-[#FF6B35]/40' : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                  {c.emoji} {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <L>Cost estimate ({trip.currency})</L>
            <input type="number" min="0" step="100" value={form.cost_estimate}
              onChange={e => setForm(f => ({ ...f, cost_estimate: e.target.value }))}
              data-testid="itin-cost" className="t-input" />
          </div>
          <div>
            <L>Notes</L>
            <textarea rows="2" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              data-testid="itin-notes" className="t-input" />
          </div>
        </div>
        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="itin-save-btn"
            className="px-4 py-2 rounded-lg bg-[#FF6B35] text-white text-xs font-bold disabled:opacity-50">
            {saving ? 'Saving…' : 'Add'}
          </button>
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

function AIItineraryModal({ api, trip, onClose, onDone }) {
  const INTEREST_OPTS = ['Beaches', 'Temples', 'Food', 'Shopping', 'Adventure', 'Nature', 'Nightlife', 'History', 'Photography', 'Family-friendly'];
  const [interests, setInterests] = useState(new Set(['Food']));
  const [pace, setPace] = useState('balanced');
  const [travelStyle, setTravelStyle] = useState('family');
  const [extraNotes, setExtraNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const toggle = (x) => setInterests(prev => {
    const n = new Set(prev);
    if (n.has(x)) n.delete(x); else n.add(x);
    return n;
  });

  const run = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.post(`/trips/${trip.trip_id}/ai-itinerary`, {
        interests: Array.from(interests),
        pace,
        travel_style: travelStyle,
        extra_notes: extraNotes,
      });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'AI generation failed.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="ai-itinerary-modal"
        className="bg-neutral-900 border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 z-10 px-5 py-3 border-b border-white/10 bg-neutral-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-purple-300" />
            <h3 className="text-sm font-bold text-white">AI Itinerary for {trip.destination}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>

        {!result && (
          <div className="p-5 space-y-3">
            {error && <div className="text-xs text-red-300 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">{error}</div>}
            <div>
              <L>Interests</L>
              <div className="flex flex-wrap gap-1.5" data-testid="ai-itin-interests">
                {INTEREST_OPTS.map(x => {
                  const on = interests.has(x);
                  return (
                    <button key={x} type="button" onClick={() => toggle(x)}
                      data-testid={`ai-itin-interest-${x.toLowerCase()}`}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${on ? 'bg-purple-500/20 text-purple-200 border-purple-500/40' : 'bg-white/5 text-neutral-400 border-white/10'}`}>
                      {x}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <L>Pace</L>
                <select value={pace} onChange={e => setPace(e.target.value)} data-testid="ai-itin-pace" className="t-input">
                  <option value="relaxed" style={{ background: '#0a0a0a' }}>Relaxed</option>
                  <option value="balanced" style={{ background: '#0a0a0a' }}>Balanced</option>
                  <option value="packed" style={{ background: '#0a0a0a' }}>Packed</option>
                </select>
              </div>
              <div>
                <L>Travel style</L>
                <select value={travelStyle} onChange={e => setTravelStyle(e.target.value)} data-testid="ai-itin-style" className="t-input">
                  <option value="family" style={{ background: '#0a0a0a' }}>Family</option>
                  <option value="couple" style={{ background: '#0a0a0a' }}>Couple / Honeymoon</option>
                  <option value="solo" style={{ background: '#0a0a0a' }}>Solo</option>
                  <option value="friends" style={{ background: '#0a0a0a' }}>Friends</option>
                  <option value="business" style={{ background: '#0a0a0a' }}>Business</option>
                </select>
              </div>
            </div>
            <div>
              <L>Anything specific?</L>
              <textarea rows="2" value={extraNotes} onChange={e => setExtraNotes(e.target.value)}
                data-testid="ai-itin-extra" className="t-input" placeholder="Kids 5 &amp; 8, like swimming, avoid late nights" />
            </div>
            <p className="text-[10px] text-neutral-500">
              Trip: {tripDays(trip)} days · {trip.party_size} traveller(s) · {trip.start_date || 'no start date'}
            </p>
          </div>
        )}

        {result && (
          <div className="p-5 space-y-3" data-testid="ai-itin-result">
            <div className="rounded-xl p-4 text-center"
              style={{ background: 'rgba(34,197,94,0.10)', border: '1px solid rgba(34,197,94,0.30)' }}>
              <p className="text-sm font-bold text-emerald-300">
                ✓ Added {result.items_added || 0} items across {result.days_added || 0} day(s)
              </p>
              <p className="text-[11px] text-neutral-400 mt-1">Tap into the itinerary tab — you can edit or delete any item.</p>
            </div>
            {result.raw_suggestion && (
              <div className="rounded-lg p-3 text-[10px] text-neutral-400" style={{ background: 'rgba(255,255,255,0.04)' }}>
                <p className="text-amber-300 font-bold mb-1">AI returned suggestions but couldn&apos;t auto-parse them:</p>
                <pre className="whitespace-pre-wrap">{result.raw_suggestion.slice(0, 500)}…</pre>
              </div>
            )}
          </div>
        )}

        <div className="sticky bottom-0 px-5 py-3 border-t border-white/10 bg-neutral-900 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 rounded-lg bg-white/5 text-neutral-300 text-xs font-semibold hover:bg-white/10">
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button onClick={run} disabled={busy} data-testid="ai-itin-run-btn"
              className="px-4 py-2 rounded-lg bg-purple-500 text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
              {busy ? (
                <>
                  <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Drafting…
                </>
              ) : (
                <><Sparkle size={11} weight="fill" /> Generate</>
              )}
            </button>
          )}
          {result && (
            <button onClick={onDone} data-testid="ai-itin-done-btn"
              className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-xs font-bold">
              View itinerary
            </button>
          )}
        </div>
        <ModalStyles />
      </div>
    </div>
  );
}

function L({ children }) {
  return <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">{children}</label>;
}
function ModalStyles() {
  return <style>{`.t-input{width:100%;padding:8px 12px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.10);font-size:13px;color:#fff;outline:none}.t-input:focus{border-color:#FF6B35}`}</style>;
}
